import { zodResolver } from "@hookform/resolvers/zod"
import { Box } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import React, { useEffect, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { usePricingDialog } from "../../hooks/usePricingDialog"

import { CancelDialog } from "@/components/common/Modals"
import { PriceChangeType } from "@/graphql/codegen/graphql"
import PriceChangeForm from "@/screens/Pricing/components/forms/PriceChangeFilterForm"
import { PricingDialogs } from "@/screens/Pricing/constants"
import { useGetPriceChangeFilterOptions } from "@/screens/Pricing/hooks/useGetPriceChangeFilterOptions"
import type { PriceChangeFilterFormValues } from "@/screens/Pricing/schema/PriceChangeFilterFormSchema"
import { PriceChangeFilterFormSchema } from "@/screens/Pricing/schema/PriceChangeFilterFormSchema"
import { usePriceChangeTablesStore, usePriceTableEditsStore } from "@/screens/Pricing/stores"
import { toPriceChangeFilterFormValues, validateInputOptions } from "@/screens/Pricing/utils/priceChangeFilter"
import { isFieldFilled, toNumberOrNull } from "@/utils/forms"

interface PriceChangeFilterProps {
  hasAppliedBulkUpdate?: boolean
}

const PriceChangeFilter: FC<PriceChangeFilterProps> = ({ hasAppliedBulkUpdate }) => {
  const navigate = useNavigate({ from: "/app/pricing/price-change" })
  const search = useSearch({
    from: "/app/pricing/_layout/price-change",
    strict: true,
  })

  const { isDialogOpen, setIsDialogOpen } = usePricingDialog()
  const hasFilterParams = Boolean(Object.keys(search).length)

  const resetTableChanges = usePriceChangeTablesStore((s) => s.reset)
  const hasUnsavedEdits = usePriceTableEditsStore((s) => s.hasEdits)
  const clearEdits = usePriceTableEditsStore((s) => s.clearEdits)
  const [pendingFilterValues, setPendingFilterValues] = useState<PriceChangeFilterFormValues | null>(null)

  const hasUnsavedChanges = hasUnsavedEdits || Boolean(hasAppliedBulkUpdate)

  const methods = useForm<PriceChangeFilterFormValues>({
    values: toPriceChangeFilterFormValues(search),
    mode: "onChange",
    resolver: zodResolver(PriceChangeFilterFormSchema),
  })

  const { watch, reset, getValues } = methods

  const [
    changeTypeFieldValue,
    productManagerFieldValue,
    supplierFieldValue,
    customerFieldValue,
    productCategoryFieldValue,
    productClassFieldValue,
    specificProductFieldValue,
    shipFromFieldValue,
    shipToFieldValue,
    effectiveDateFieldValue,
  ] = watch([
    "changeType",
    "productManagerId",
    "supplierId",
    "customerIds",
    "productCategoryId",
    "productClassId",
    "specificProductIds",
    "shipFromId",
    "shipToId",
    "effectiveDate",
  ])

  const { filterOptions, fetching: isFetching } = useGetPriceChangeFilterOptions({
    changeType: changeTypeFieldValue as PriceChangeType,
    supplierId: supplierFieldValue ?? undefined,
    customerIds: customerFieldValue ?? undefined,
    productCategoryId: productCategoryFieldValue ?? undefined,
    productClassId: productClassFieldValue ?? undefined,
    productManagerId: productManagerFieldValue ?? undefined,
    specificProductIds: specificProductFieldValue
      ?.map((specificProductId) => toNumberOrNull(specificProductId))
      .filter((v) => v !== null),

    shipFromId: shipFromFieldValue ?? undefined,
    shipToId:
      changeTypeFieldValue === PriceChangeType.PurchasingCost && shipToFieldValue ? shipToFieldValue : undefined,
  })

  const canApplyFilters = useMemo(() => {
    const additionalFilters = [
      supplierFieldValue,
      customerFieldValue?.length,
      productCategoryFieldValue,
      productClassFieldValue,
      specificProductFieldValue?.length,
      shipFromFieldValue,
      shipToFieldValue,
      effectiveDateFieldValue,
      productManagerFieldValue,
    ]

    return additionalFilters.some(Boolean)
  }, [
    supplierFieldValue,
    customerFieldValue?.length,
    productCategoryFieldValue,
    productClassFieldValue,
    specificProductFieldValue?.length,
    shipFromFieldValue,
    shipToFieldValue,
    effectiveDateFieldValue,
    productManagerFieldValue,
  ])

  useEffect(() => {
    const values = getValues()

    if (
      !values.changeType ||
      (values.changeType === PriceChangeType.SellingPrice && !values.productManagerId) ||
      !filterOptions ||
      isFetching
    ) {
      return
    }

    const nextValues: PriceChangeFilterFormValues = {
      ...values,
      supplierId: validateInputOptions({ data: values.supplierId, options: filterOptions?.suppliers }),
      customerIds: validateInputOptions({ data: values.customerIds, options: filterOptions?.customers }),
      productCategoryId: validateInputOptions({
        data: values.productCategoryId,
        options: filterOptions?.productCategories,
      }),
      productClassId: validateInputOptions({ data: values.productClassId, options: filterOptions?.productClasses }),
      specificProductIds: validateInputOptions({ data: values.specificProductIds, options: filterOptions?.products }),
      productManagerId: validateInputOptions({
        data: values.productManagerId,
        options: filterOptions?.productManagers,
      }),
      shipFromId: validateInputOptions({
        data: values.shipFromId,
        options: filterOptions?.shipFromWarehouseAreas,
      }),

      shipToId: validateInputOptions({
        data: values.shipToId,
        options: filterOptions?.shipToLocations,
      }),
    }

    reset(nextValues, {
      keepDirty: true,
      keepTouched: true,
    })
  }, [isFetching, filterOptions, getValues, reset])

  const handleFilterSubmit = async (data: PriceChangeFilterFormValues) => {
    const normalizedData = {
      customerIds: isFieldFilled(data.customerIds) ? (data.customerIds as string[]) : undefined,
      specificProductIds: isFieldFilled(data.specificProductIds) ? (data.specificProductIds as string[]) : undefined,
      supplierId: isFieldFilled(data.supplierId) ? (data.supplierId as string) : undefined,
      productCategoryId: isFieldFilled(data.productCategoryId) ? (data.productCategoryId as string) : undefined,
      productClassId: isFieldFilled(data.productClassId) ? (data.productClassId as string) : undefined,
      shipFromId: isFieldFilled(data.shipFromId) ? (data.shipFromId as string) : undefined,
      shipToId:
        data.changeType === PriceChangeType.PurchasingCost && isFieldFilled(data.shipToId)
          ? (data.shipToId as string)
          : undefined,
      productManagerId: isFieldFilled(data.productManagerId) ? (data.productManagerId as string) : undefined,
      effectiveDate: isFieldFilled(data?.effectiveDate) ? (data.effectiveDate as string) : undefined,
    }

    await navigate({
      search: {
        ...search,
        ...normalizedData,
        changeType: data.changeType as PriceChangeType,
        page: undefined,
      },
      replace: true,
      ignoreBlocker: true,
    })
  }

  const handleApplyFilters = async (data: PriceChangeFilterFormValues) => {
    if (hasUnsavedChanges) {
      setPendingFilterValues(data)
      setIsDialogOpen(PricingDialogs.ApplyPricingFiltersConfirmation)
      return
    }

    await handleFilterSubmit(data)
  }

  const handleApplyFiltersConfirm = async () => {
    if (!pendingFilterValues) return

    resetTableChanges()
    clearEdits()
    await handleFilterSubmit(pendingFilterValues)
    setPendingFilterValues(null)
    setIsDialogOpen(null)
  }

  const handleApplyFiltersCancel = () => {
    setPendingFilterValues(null)
    setIsDialogOpen(null)
  }

  const handleResetFilters = async () => {
    methods.reset()
    await navigate({
      search: (prev) => ({
        ...prev,
        changeType: undefined,
        supplierId: undefined,
        customerIds: undefined,
        productCategoryId: undefined,
        productManagerId: undefined,
        productClassId: undefined,
        specificProductIds: undefined,
        shipFromId: undefined,
        shipToId: undefined,
        effectiveDate: undefined,
        page: undefined,
      }),
      replace: true,
    })
  }

  const handleClear = async () => {
    if (hasUnsavedChanges) {
      setIsDialogOpen(PricingDialogs.ClearAllFiltersConfirmation)
      return
    }

    await handleResetFilters()
  }

  const handleClearConfirm = async () => {
    resetTableChanges()
    clearEdits()
    setIsDialogOpen(null)

    await handleResetFilters()
  }

  return (
    <Box className='mt-6'>
      <FormProvider {...methods}>
        <PriceChangeForm
          onSubmit={handleApplyFilters}
          onClearAll={handleClear}
          hasFilterParams={hasFilterParams}
          canApplyFilters={canApplyFilters}
          filterOptions={filterOptions}
        />
      </FormProvider>

      {isDialogOpen === PricingDialogs.ApplyPricingFiltersConfirmation && (
        <CancelDialog
          title='Altering filters will discard your changes.'
          message='You’ve made edits to the current results. Applying new filters will reload the content and remove those changes.'
          confirmLabel='Apply filters anyway'
          closeLabel='Cancel'
          onClose={handleApplyFiltersCancel}
          onConfirm={handleApplyFiltersConfirm}
        />
      )}
      {isDialogOpen === PricingDialogs.ClearAllFiltersConfirmation && (
        <CancelDialog
          title='Clear filters will discard your changes.'
          message='You’ve made edits to the current results. Clearing filters will remove those changes.'
          confirmLabel='Yes, Clear All'
          closeLabel='Cancel'
          onClose={() => {
            setIsDialogOpen(null)
          }}
          onConfirm={handleClearConfirm}
        />
      )}
    </Box>
  )
}

export default PriceChangeFilter
